import React, { useState } from 'react';
import theme from '../../../theme/globalTheme';
import { Send, Mail, User, Clock, Euro } from 'lucide-react';

const JetskiEmailSender = () => {
  const [formData, setFormData] = useState({
    email: '',
    language: 'french',
    customerType: 'resident',
    duration: '1h',
    customPrice: ''
  });

  const [emailPreview, setEmailPreview] = useState('');
  const [htmlEmailPreview, setHtmlEmailPreview] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  // Pricing structure
  const pricing = {
    resident: {
      '1h': 100,
      '1h30': 130
    },
    tourist: {
      '1h': 140,
      '1h30': 170
    }
  };

  // Email templates
  const emailTemplates = {
    french: {
      subject: "Réservation - Jetski MASTERSKI PILOU",
      greeting: "Bonjour,",
      thankYou: "Merci, vous trouverez ci-dessous notre lien de paiement afin de régler et confirmer votre réservation afin de réserver au moins un jetski",
      price: "Prix pour {duration}: {price}€",
      paymentLink: "https://jepaieenligne.systempay.fr/MASTERSKI_PILOU",
      closing: "Bonne journée,"
    },
    english: {
      subject: "Booking Confirmation - Jetski MASTERSKI PILOU",
      greeting: "Hello,",
      thankYou: "Thank you. Please find below our payment link to settle and confirm your booking — a payment for at least one jetski is required to secure the reservation:",
      price: "Price for {duration}: €{price}",
      paymentLink: "https://jepaieenligne.systempay.fr/MASTERSKI_PILOU",
      closing: "Best Regards,"
    }
  };

  const generateEmailContent = () => {
    const template = emailTemplates[formData.language];
    const price = formData.customPrice || pricing[formData.customerType][formData.duration];
    
    // Format duration text
    const durationText = formData.duration === '1h30' ? 
      '1H30/island tour depending on sea condition' : 
      (formData.language === 'french' ? '1H' : '1 Hour');
    
    const priceText = template.price
      .replace('{duration}', durationText)
      .replace('{price}', price);

    // Text version with line breaks
    const textContent = `${template.greeting}

${template.thankYou}

${priceText}

${template.paymentLink}

${template.closing}`;

    // HTML version with proper formatting
    const htmlContent = `${template.greeting}<br/><br/>
${template.thankYou}<br/><br/>
<strong>${priceText}</strong><br/><br/>
<a href="${template.paymentLink}" style="color:#007bff;text-decoration:none;">${template.paymentLink}</a><br/><br/>
${template.closing}`;

    return { textContent, htmlContent };
  };

  const handleInputChange = (field, value) => {
    const newFormData = { ...formData, [field]: value };
    setFormData(newFormData);
    updateEmailPreview(newFormData);
  };

  const updateEmailPreview = (data = formData) => {
    const { textContent, htmlContent } = generateEmailContent();
    setEmailPreview(textContent);
    setHtmlEmailPreview(htmlContent);
  };

  React.useEffect(() => {
    updateEmailPreview();
  }, [formData]);

  const getCurrentPrice = () => {
    return formData.customPrice || pricing[formData.customerType][formData.duration];
  };

  const sendEmail = async () => {
    if (!formData.email) {
      alert('Veuillez saisir une adresse email / Please enter an email address');
      return;
    }

    setIsLoading(true);
    
    try {
      const { textContent, htmlContent } = generateEmailContent();
      
      const webhookData = {
        to: formData.email,
        subject: emailTemplates[formData.language].subject,
        content: textContent,
        htmlContent: htmlContent,
        customerType: formData.customerType,
        duration: formData.duration,
        price: getCurrentPrice(),
        language: formData.language
      };

      // Make webhook URL - configured and ready
      const makeWebhookUrl = 'https://hook.eu2.make.com/pfmt5x1gf2d2ls7fbj78mw443o2fw429';
      
      // Send data to Make webhook
      const response = await fetch(makeWebhookUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(webhookData)
      });
      
      if (response.ok) {
        alert('Email envoyé avec succès! / Email sent successfully!');
        // Reset form after successful send
        setFormData({
          email: '',
          language: 'french',
          customerType: 'resident',
          duration: '1h',
          customPrice: ''
        });
      } else {
        throw new Error('Failed to send email');
      }
      
    } catch (error) {
      console.error('Error sending email:', error);
      alert('Erreur lors de l\'envoi / Error sending email');
    } finally {
      setIsLoading(false);
    }
  };

  // Inline styles - Uber style (grey, black, white)
  const styles = {
    container: {
      minHeight: '100vh',
      backgroundColor: theme.colors.glassLight,
      padding: '20px',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
    },
    mainWrapper: {
      maxWidth: '1200px',
      margin: '0 auto'
    },
    header: {
      backgroundColor: '#000000',
      color: 'white',
      padding: '30px',
      borderRadius: '8px',
      marginBottom: '30px',
      boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
    },
    headerTitle: {
      fontSize: '32px',
      fontWeight: '700',
      display: 'flex',
      alignItems: 'center',
      gap: '15px',
      margin: 0
    },
    headerSubtitle: {
      marginTop: '8px',
      fontSize: '16px',
      opacity: 0.8,
      fontWeight: '400'
    },
    gridContainer: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(500px, 1fr))',
      gap: '24px'
    },
    card: {
      backgroundColor: theme.colors.glassMedium,
      padding: '32px',
      borderRadius: '8px',
      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
      border: '1px solid #e5e5e5'
    },
    cardTitle: {
      fontSize: '24px',
      fontWeight: '600',
      marginBottom: '24px',
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      color: '#111'
    },
    formGroup: {
      marginBottom: '20px'
    },
    label: {
      display: 'block',
      fontSize: '14px',
      fontWeight: '500',
      color: theme.colors.textPrimary,
      marginBottom: '8px'
    },
    input: {
      width: '100%',
      padding: '12px 16px',
      border: '1px solid #ddd',
      borderRadius: '4px',
      fontSize: '16px',
      transition: 'border-color 0.2s ease',
      outline: 'none',
      boxSizing: 'border-box',
      backgroundColor: theme.colors.glassMedium
    },
    inputFocus: {
      borderColor: '#000'
    },
    select: {
      width: '100%',
      padding: '12px 16px',
      border: '1px solid #ddd',
      borderRadius: '4px',
      fontSize: '16px',
      backgroundColor: theme.colors.glassMedium,
      cursor: 'pointer',
      outline: 'none',
      boxSizing: 'border-box'
    },
    priceDisplay: {
      padding: '16px',
      backgroundColor: '#f8f8f8',
      border: '1px solid #e5e5e5',
      borderRadius: '4px',
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      color: '#111'
    },
    button: {
      width: '100%',
      padding: '16px 24px',
      backgroundColor: '#000000',
      color: 'white',
      border: 'none',
      borderRadius: '4px',
      fontSize: '16px',
      fontWeight: '500',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '8px',
      transition: 'background-color 0.2s ease',
      outline: 'none'
    },
    buttonHover: {
      backgroundColor: '#333'
    },
    buttonDisabled: {
      backgroundColor: '#ccc',
      cursor: 'not-allowed'
    },
    pricingGrid: {
      display: 'grid',
      gridTemplateColumns: '1fr 1fr',
      gap: '16px',
      marginTop: '16px'
    },
    pricingCard: {
      padding: '16px',
      borderRadius: '4px',
      border: '1px solid #e5e5e5',
      textAlign: 'center',
      backgroundColor: theme.colors.glassLight
    },
    residentCard: {
      borderColor: '#666'
    },
    touristCard: {
      borderColor: '#666'
    },
    previewBox: {
      backgroundColor: theme.colors.glassLight,
      padding: '20px',
      border: '1px solid #e5e5e5',
      borderRadius: '4px',
      minHeight: '200px'
    },
    preText: {
      whiteSpace: 'pre-wrap',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      lineHeight: '1.6',
      color: theme.colors.textPrimary,
      margin: 0
    },
    instructionsBox: {
      backgroundColor: '#f8f8f8',
      border: '1px solid #e5e5e5',
      padding: '20px',
      borderRadius: '4px',
      marginTop: '24px'
    },
    instructionsList: {
      margin: '10px 0',
      paddingLeft: '20px',
      color: '#555'
    },
    metaText: {
      fontSize: '14px',
      color: '#666',
      marginBottom: '5px'
    },
    separator: {
      borderTop: '1px solid #e5e5e5',
      paddingTop: '16px',
      marginTop: '16px'
    }
  };

  return (
    <div style={{ minHeight: '100vh', background: theme.colors.bgDark, padding: '20px' }} style={styles.container}>
      <div style={styles.mainWrapper}>
        {/* Header */}


        <div style={styles.gridContainer}>
          {/* Configuration Panel */}
          <div>
            <div style={styles.card}>
              <h2 style={styles.cardTitle}>
                <User size={24} />
                Configuration
              </h2>
              
              {/* Email Input */}
              <div style={styles.formGroup}>
                <label style={styles.label}>
                  📧 Email du client / Customer Email
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  placeholder="client@example.com"
                  style={styles.input}
                />
              </div>

              {/* Language Selection */}
              <div style={styles.formGroup}>
                <label style={styles.label}>
                  🌐 Langue / Language
                </label>
                <select
                  value={formData.language}
                  onChange={(e) => handleInputChange('language', e.target.value)}
                  style={styles.select}
                >
                  <option value="french">🇫🇷 Français</option>
                  <option value="english">🇬🇧 English</option>
                </select>
              </div>

              {/* Customer Type */}
              <div style={styles.formGroup}>
                <label style={styles.label}>
                  👤 Type de client / Customer Type
                </label>
                <select
                  value={formData.customerType}
                  onChange={(e) => handleInputChange('customerType', e.target.value)}
                  style={styles.select}
                >
                  <option value="resident">🏠 Résident / Resident</option>
                  <option value="tourist">✈️ Touriste / Tourist</option>
                </select>
              </div>

              {/* Duration */}
              <div style={styles.formGroup}>
                <label style={styles.label}>
                  ⏰ Durée / Duration
                </label>
                <select
                  value={formData.duration}
                  onChange={(e) => handleInputChange('duration', e.target.value)}
                  style={styles.select}
                >
                  <option value="1h">1 Heure / 1 Hour</option>
                  <option value="1h30">🏝️ 1H30/island tour depending on sea condition</option>
                </select>
              </div>

              {/* Current Price Display */}
              <div style={styles.formGroup}>
                <div style={styles.priceDisplay}>
                  <Euro size={24} />
                  <div>
                    <div style={{fontSize: '20px', fontWeight: 'bold'}}>€{getCurrentPrice()}</div>
                    <div style={{fontSize: '14px'}}>Prix automatique / Auto Price</div>
                  </div>
                </div>
              </div>

              {/* Custom Price Override */}
              <div style={styles.formGroup}>
                <label style={styles.label}>
                  💰 Prix personnalisé / Custom Price (optionnel)
                </label>
                <input
                  type="number"
                  value={formData.customPrice}
                  onChange={(e) => handleInputChange('customPrice', e.target.value)}
                  placeholder="Laisser vide pour prix automatique"
                  style={styles.input}
                />
              </div>

              {/* Send Button */}
              <button
                onClick={sendEmail}
                disabled={isLoading || !formData.email}
                style={{
                  ...styles.button,
                  ...(isLoading || !formData.email ? styles.buttonDisabled : {})
                }}
              >
                {isLoading ? (
                  <div style={{
                    width: '20px',
                    height: '20px',
                    border: '2px solid white',
                    borderTop: '2px solid transparent',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite'
                  }}></div>
                ) : (
                  <>
                    <Send size={20} />
                    Envoyer l'email / Send Email
                  </>
                )}
              </button>
            </div>

            {/* Pricing Table */}
            <div style={{...styles.card, marginTop: '20px'}}>
              <h3 style={{...styles.cardTitle, fontSize: '20px'}}>
                💰 Tarifs / Pricing
              </h3>
              <div style={styles.pricingGrid}>
                <div style={{...styles.pricingCard, ...styles.residentCard}}>
                  <div style={{fontWeight: 'bold', marginBottom: '8px'}}>
                    🏠 Résident / Resident
                  </div>
                  <div>1H: <strong>€100</strong></div>
                  <div>1H30: <strong>€130</strong></div>
                </div>
                <div style={{...styles.pricingCard, ...styles.touristCard}}>
                  <div style={{fontWeight: 'bold', marginBottom: '8px'}}>
                    ✈️ Touriste / Tourist
                  </div>
                  <div>1H: <strong>€140</strong></div>
                  <div>1H30/island tour: <strong>€170</strong></div>
                </div>
              </div>
            </div>
          </div>

          {/* Email Preview */}
          <div>
            <div style={styles.card}>
              <h2 style={styles.cardTitle}>
                📧 Aperçu de l'email / Email Preview
              </h2>
              <div style={styles.previewBox}>
                <div style={{fontSize: '14px', color: '#666', marginBottom: '5px'}}>
                  <strong>À / To:</strong> {formData.email || 'client@example.com'}
                </div>
                <div style={{fontSize: '14px', color: '#666', marginBottom: '15px'}}>
                  <strong>Sujet / Subject:</strong> {emailTemplates[formData.language].subject}
                </div>
                <div style={{borderTop: '2px solid #ddd', paddingTop: '15px'}}>
                  <pre style={styles.preText}>
                    {emailPreview}
                  </pre>
                </div>
              </div>
            </div>

            {/* HTML Preview */}
            <div style={{...styles.card, marginTop: '20px'}}>
              <h2 style={{...styles.cardTitle, fontSize: '20px'}}>
                🎨 Aperçu HTML / HTML Preview
              </h2>
              <div style={styles.previewBox}>
                <div 
                  style={{lineHeight: '1.6', color: theme.colors.textPrimary}}
                  dangerouslySetInnerHTML={{ __html: htmlEmailPreview }}
                />
              </div>
            </div>

            {/* Instructions */}
            <div style={styles.instructionsBox}>
              <h3 style={{fontWeight: 'bold', color: '#e65100', marginBottom: '10px'}}>
                ⚙️ Configuration Make
              </h3>
              <p style={{fontSize: '14px', color: '#bf360c', marginBottom: '10px'}}>
                <strong>Dans votre module "Send an Email" de Make :</strong>
              </p>
              <ul style={{...styles.instructionsList, fontSize: '14px', color: '#bf360c'}}>
                <li><strong>Content Type:</strong> HTML</li>
                <li><strong>To:</strong> {`{{1.to}}`}</li>
                <li><strong>Subject:</strong> {`{{1.subject}}`}</li>
                <li><strong>Content:</strong> {`{{1.htmlContent}}`}</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default JetskiEmailSender;